/**
 * getUserMedia constraints for the microphone.
 *
 * Echo cancellation and auto gain are NATIVE WebRTC constraints — the browser's
 * AEC has access to the render-side reference signal and nothing in JavaScript
 * can beat it, so these are exposed as settings rather than reimplemented.
 *
 * Noise suppression is the exception: when RNNoise is wanted we still ask for
 * the native one at capture time (so audio is usable during the ~200 ms the
 * WASM takes to load) and then turn it off with `applyConstraints` once the
 * worklet is live. Two denoisers in series smear consonants.
 */
import { AUDIO_SETTINGS } from '@cyphex/types';
import type { AudioProcessingPreferences } from '../settings/preferences';

export function micConstraints(
  preferences: AudioProcessingPreferences,
  deviceId?: string,
): MediaTrackConstraints {
  return {
    channelCount: AUDIO_SETTINGS.channelCount,
    echoCancellation: preferences.echoCancellation,
    autoGainControl: preferences.autoGainControl,
    noiseSuppression: preferences.noiseSuppression !== 'off',
    ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
  };
}

/**
 * Re-applies the native processing flags to a live track. Cheap and instant —
 * changing echo cancellation must not cost a getUserMedia round trip, which
 * would drop audio for a beat and re-trigger the OS mic indicator.
 *
 * `nativeNoiseSuppression` is decided by the caller: it is false while RNNoise
 * is doing the work, true otherwise.
 */
export async function applyNativeProcessing(
  track: MediaStreamTrack,
  preferences: AudioProcessingPreferences,
  nativeNoiseSuppression: boolean,
): Promise<void> {
  await track
    .applyConstraints({
      echoCancellation: preferences.echoCancellation,
      autoGainControl: preferences.autoGainControl,
      noiseSuppression: nativeNoiseSuppression,
    })
    .catch(() => undefined);
}
